import { Item, Menu, Schedule, Section, SuperSectionVM } from "../models/menu.model";
import { DateTime } from 'luxon';
import { ItemStatus } from '../shared/enums';

export function isSectionActive(section: Section, now: DateTime = DateTime.now()): boolean {
    const schedules = section.schedules;
    if (!schedules || schedules.length === 0) return true;
    return schedules.some(schedule => isActive(schedule, now));
}

// The menu as it should currently be shown and priced: sections (and sub-sections) whose
// schedules don't match "now" are dropped entirely, and items with an active priced schedule
// (e.g. happy hour) get that price. This is the one place schedule logic gets applied — everything
// downstream (superSections, search, cart sync) reads its input already resolved.
export function resolveMenu(menu: Menu, now: DateTime = DateTime.now()): Menu {
    return { ...menu, sections: resolveSections(menu.sections, now) };
}

function resolveSections(sections: Section[], now: DateTime): Section[] {
    return sections
        .filter(section => isSectionActive(section, now))
        .map(section => ({
            ...section,
            items: section.items.map(item => resolveItemPricing(item, now)),
            sub_sections: resolveSections(section.sub_sections, now),
        }));
}

// Applies a currently-active priced schedule (e.g. happy hour) to the item, leaving it
// untouched (same reference) when no such schedule is active.
export function resolveItemPricing(item: Item, now: DateTime = DateTime.now()): Item {
    const activeSchedule = item.schedules.find(schedule => schedule.price !== undefined && isActive(schedule, now));
    return activeSchedule ? { ...item, price: activeSchedule.price! } : item;
}

// The instant of the next schedule start/end across the whole menu (sections, sub-sections,
// and items), so callers can wake up exactly when something should flip instead of polling.
// Returns null when nothing on the menu is schedule-driven at all.
export function nextScheduleBoundary(menu: Menu, now: DateTime = DateTime.now()): DateTime | null {
    let earliest: DateTime | null = null;

    for (const schedule of collectSchedules(menu.sections)) {
        const zonedNow = now.setZone(schedule.timezone);
        for (const window of scheduleWindows(schedule, zonedNow)) {
            for (const boundary of [window.start, window.end]) {
                if (boundary > zonedNow && (!earliest || boundary < earliest)) {
                    earliest = boundary;
                }
            }
        }
    }

    return earliest;
}

function collectSchedules(sections: Section[]): Schedule[] {
    const schedules: Schedule[] = [];

    const visit = (section: Section): void => {
        schedules.push(...section.schedules);
        section.items.forEach(item => schedules.push(...item.schedules));
        section.sub_sections.forEach(visit);
    };

    sections.forEach(visit);
    return schedules;
}

// Whether a section (or any of its sub-sections) has at least one item that isn't sold out —
// sections/super-sections with none of these should stay hidden rather than link to an empty page.
export function hasEnabledItems(section: Section): boolean {
    return section.items.some(item => item.status !== ItemStatus.Unavailable)
        || section.sub_sections.some(sub => hasEnabledItems(sub));
}

export function applyItemFilters(section: Section, filterIds: string[]): Section {
    if (filterIds.length === 0) return section;

    return {
        ...section,
        items: section.items.filter(item => filterIds.every(id => item.filters?.includes(id))),
        sub_sections: section.sub_sections.map(sub => applyItemFilters(sub, filterIds)),
    };
}

export function toSuperSectionVM(section: Section): SuperSectionVM {
    return {
        id: section.id,
        name: section.name,
        description: section.description,
        logo_url: section.logo_url,
        banner_url: section.banner_url,
    };
}

export function findItemById(sections: Section[], id: string): Item | undefined {
    return getAllItems(sections).find(item => item.id === id);
}

// Flattens the items of every active section (and sub-section) into a single deduped list
export function getAllItems(sections: Section[]): Item[] {
    const items: Item[] = [];
    const ids = new Set<string>();

    const visit = (section: Section): void => {
        if (!isSectionActive(section)) return;
        section.items.forEach(item => {
            if (!ids.has(item.id)) {
                ids.add(item.id);
                items.push(item);
            }
        });
        section.sub_sections.forEach(visit);
    };

    sections.forEach(visit);
    return items;
}

// Rank used to order search results by match quality: name -> description -> modifiers/category.
function matchPriority(item: Item, q: string): number {
    if (item.name.toLowerCase().includes(q)) return 0;
    if (item.description.toLowerCase().includes(q)) return 1;
    return 2;
}

// Matches by name -> description -> modifiers/category, in that priority order, with dupes removed
export function filterItems(items: Item[], query: string): Item[] {
    if (!query) return [];
    const q = query.toLowerCase();

    const byName = items.filter(item => item.name.toLowerCase().includes(q));
    const byDescription = items.filter(item => item.description.toLowerCase().includes(q));
    const byModifiersOrCategory = items.filter(item => matchesModifiersOrCategory(item, q));

    const result = [...byName];
    [...byDescription, ...byModifiersOrCategory].forEach(item => {
        if (!result.includes(item)) result.push(item);
    });
    return result;
}

export interface SearchItemGroup {
    superSectionId: string;
    superSectionName: string;
    subSectionId?: string;
    subSectionName?: string;
    isOptionalSubSection?: boolean;
    items: Item[];
}

// One group per (super-section, sub-section-or-none) pair with at least one active item.
// Unlike getAllItems()'s flat, deduped list, this keeps exactly where each item came from -
// the same item listed in two different sub-sections becomes two separate groups, so a
// search result can carry the correct subSectionId instead of one being silently dropped.
export function getSearchGroups(sections: Section[]): SearchItemGroup[] {
    const groups: SearchItemGroup[] = [];

    sections.forEach(section => {
        if (!isSectionActive(section)) return;

        if (section.items.length) {
            groups.push({ superSectionId: section.id, superSectionName: section.name, items: section.items });
        }

        (section.sub_sections ?? []).forEach(sub => {
            if (!isSectionActive(sub) || !sub.items.length) return;
            groups.push({
                superSectionId: section.id,
                superSectionName: section.name,
                subSectionId: sub.id,
                subSectionName: sub.name,
                isOptionalSubSection: !sub.required,
                items: sub.items,
            });
        });
    });

    return groups;
}

// Filters each group's items with the same matching filterItems() uses, dropping any group
// left with nothing to show.
export function filterSearchGroups(groups: SearchItemGroup[], query: string): SearchItemGroup[] {
    if (!query) return [];
    return groups
        .map(group => ({ ...group, items: filterItems(group.items, query) }))
        .filter(group => group.items.length > 0);
}

export interface SearchResultItem {
    item: Item;
    subSectionId?: string;
    isOptionalSubSection?: boolean;
    superSectionId: string;
    superSectionName: string;
}

// Flattens grouped results back into one list (no headings), tagging each occurrence with
// the subSectionId and super-section it actually came from, then ranks by match quality
// (name -> description -> modifier/category) across all sections - a name match in a later
// section still outranks a category match in an earlier one. Sort is stable, so ties keep
// their original section order.
export function flattenSearchResults(groups: SearchItemGroup[], query: string): SearchResultItem[] {
    const q = query.toLowerCase();
    return groups
        .flatMap(group => group.items.map(item => ({
            item,
            subSectionId: group.subSectionId,
            isOptionalSubSection: group.isOptionalSubSection,
            superSectionId: group.superSectionId,
            superSectionName: group.superSectionName,
        })))
        .sort((a, b) => matchPriority(a.item, q) - matchPriority(b.item, q));
}

// The super-section a given sub-section id belongs to, for display purposes (e.g. labeling a
// cart line when the same item is duplicated across sections) - general menu structure, not
// combo-specific.
export function findSuperSectionFor(sections: Section[], subSectionId: string): { id: string; name: string } | undefined {
    const section = sections.find(s => (s.sub_sections ?? []).some(sub => sub.id === subSectionId));
    return section ? { id: section.id, name: section.name } : undefined;
}

// Sections matched by name, or that (recursively) contain a matching item — sold-out-only
// sections are excluded so a search result never links to an empty page.
export function filterSections(sections: Section[], query: string): Section[] {
    if (!query) return [];
    const q = query.toLowerCase();

    return sections
        .filter(section => isSectionActive(section))
        .filter(hasEnabledItems)
        .filter(section => section.name.toLowerCase().includes(q) || sectionContainsMatch(section, q));
}

function sectionContainsMatch(section: Section, q: string): boolean {
    if (section.items.some(item => matchesItem(item, q))) return true;
    return section.sub_sections.some(sub => isSectionActive(sub) && sectionContainsMatch(sub, q));
}

function matchesItem(item: Item, q: string): boolean {
    return item.name.toLowerCase().includes(q)
        || item.description.toLowerCase().includes(q)
        || matchesModifiersOrCategory(item, q);
}

function matchesModifiersOrCategory(item: Item, q: string): boolean {
    return item.category.toLowerCase().includes(q)
        || item.modifier_groups.some(mg => mg.modifiers.some(m => m.name.toLowerCase().includes(q)));
}

export interface ScheduleWindow {
    start: DateTime;
    end: DateTime;
}

// A weekly schedule ("Monday, 22:00-02:00") recurs, so this returns the concrete start/end
// instants for the occurrence anchored to each day from yesterday through next week relative
// to `now` — enough range to find both "is it active right now" and "what's the next boundary".
// An end time at or before the start time is treated as spanning into the next calendar day.
export function scheduleWindows(schedule: Schedule, now: DateTime): ScheduleWindow[] {
    const windows: ScheduleWindow[] = [];

    for (let dayOffset = -1; dayOffset <= 7; dayOffset++) {
        const anchor = now.plus({ days: dayOffset });
        if (anchor.weekday !== schedule.week_day) continue;

        const start = setTimeOfDay(anchor, schedule.start_time);
        let end = setTimeOfDay(anchor, schedule.end_time);
        if (end <= start) end = end.plus({ days: 1 });

        windows.push({ start, end });
    }

    return windows;
}

function setTimeOfDay(date: DateTime, time: string): DateTime {
    const [hour, minute] = time.split(':').map(Number);
    return date.set({ hour, minute, second: 0, millisecond: 0 });
}

export function isActive(schedule: Schedule, now: DateTime = DateTime.now()): boolean {
    const zonedNow = now.setZone(schedule.timezone);
    return scheduleWindows(schedule, zonedNow).some(w => zonedNow >= w.start && zonedNow < w.end);
}